/**
 * Email-OTP auth for the customer web app — the in-app equivalent of mobile's
 * `authService` email flow (specs/001-free-signup/auth-otp-pivot.md).
 *
 * The web previously bounced every sign-in through Zitadel's hosted login
 * (oidc-client-ts `signinRedirect`). That hosted page isn't always reachable
 * (404s in local/dev), and it's a different experience from mobile. This module
 * drives the same four backend endpoints mobile uses — start / verify / resend
 * / complete-profile — so the user enters an email, receives a code, and never
 * leaves the SPA.
 *
 * Tokens live in localStorage (not SecureStore — this is a browser). They're
 * read by AuthProvider's apiClient token provider, and refreshed against
 * Zitadel's token endpoint (discovered from `/api/v1/auth/config`) the same way
 * mobile refreshes via `AuthSession.refreshAsync`.
 */
import { getApiUrl } from '../../lib/runtimeConfig';

const TOKENS_KEY = 'streamko-email-tokens';
const USER_KEY = 'streamko-email-user';

/** Refresh this many ms before hard expiry so the API never sees a stale token. */
const REFRESH_SKEW_MS = 30_000;

function apiBase(): string {
  return `${getApiUrl() || 'http://localhost:5001'}/api/v1`;
}

// ── Contracts (mirror mobile's authService) ─────────────────────────────────

export interface EmailAuthUser {
  id: string;
  email: string;
  fullName: string;
  emailVerified: boolean;
}

export interface EmailAuthOrganization {
  id: string;
  name: string;
  status: string;
  createdAt: string;
}

export interface StartEmailResponse {
  ticketId: string;
  expiresIn: number;
  isNewUser: boolean;
  /** DEV ONLY — populated when the backend echoes the code back. */
  devCode?: string | null;
}

export interface EmailOtpAuthResponse {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresIn: number;
  user: EmailAuthUser;
  organization: EmailAuthOrganization | null;
  /** True when the caller must collect first/last/org name next. */
  needsProfile: boolean;
}

export interface CompleteProfileResponse {
  user: EmailAuthUser;
  organization: EmailAuthOrganization;
}

interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  /** Epoch ms. */
  expiresAt: number;
}

/** Backend error envelope: `{ error, message, details? }`. */
export class EmailAuthError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'EmailAuthError';
  }
}

// ── Token storage ───────────────────────────────────────────────────────────

function loadTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

function storeTokens(tokens: StoredTokens): void {
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

function clearStoredTokens(): void {
  localStorage.removeItem(TOKENS_KEY);
}

function persistFromAuthResponse(accessToken: string, refreshToken: string | undefined, expiresIn: number, idToken?: string): void {
  storeTokens({
    accessToken,
    // Keep only a non-empty refresh token — `''` is useless and would just
    // fail the grant.
    refreshToken: refreshToken || undefined,
    idToken,
    expiresAt: Date.now() + expiresIn * 1000,
  });
}

export function getStoredUser(): EmailAuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as EmailAuthUser) : null;
  } catch {
    return null;
  }
}

function setStoredUser(u: EmailAuthUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify({
    id: u.id,
    email: u.email,
    fullName: u.fullName,
    emailVerified: u.emailVerified,
  }));
}

/** True when an email-OTP session token is present (may still need refreshing). */
export function hasEmailSession(): boolean {
  return loadTokens() !== null;
}

/** Clear the email session (tokens + cached user + discovery). */
export function signOutEmail(): void {
  clearStoredTokens();
  localStorage.removeItem(USER_KEY);
  _discovery = null;
}

// ── HTTP ────────────────────────────────────────────────────────────────────

async function request<T>(
  path: string,
  body: unknown,
  withAuth = false,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (withAuth) {
    const token = await getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${apiBase()}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const raw = await res.text();
    let parsed: { error?: string; message?: string; details?: unknown } = {};
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = { message: raw || res.statusText };
    }
    throw new EmailAuthError(
      parsed.error || 'request_failed',
      parsed.message || `Request failed (${res.status})`,
      parsed.details,
    );
  }

  return res.json() as Promise<T>;
}

/** POST /auth/email/start — begins signup or sign-in (backend decides from email). */
export function startEmail(email: string): Promise<StartEmailResponse> {
  return request<StartEmailResponse>('/auth/email/start', { email });
}

/** POST /auth/email/verify — exchanges the OTP for tokens, persisting them. */
export async function verifyEmailOtp(input: { ticketId: string; code: string }): Promise<EmailOtpAuthResponse> {
  const data = await request<EmailOtpAuthResponse>('/auth/email/verify', input);
  persistFromAuthResponse(data.accessToken, data.refreshToken, data.expiresIn, data.idToken);
  if (data.user) setStoredUser(data.user);
  return data;
}

/** POST /auth/email/resend-otp — re-sends the code for an existing ticket. */
export function resendEmailOtp(ticketId: string): Promise<StartEmailResponse> {
  return request<StartEmailResponse>('/auth/email/resend-otp', { ticketId });
}

// ── Social login (Zitadel IdP intents) ──────────────────────────────────────
//
// The web equivalent of mobile's authService.socialStart/socialFinish. Instead
// of Zitadel's hosted login (signinRedirect → /ui/v2/login chooser), the backend
// starts an IdP intent and returns the provider's own authorization URL. The SPA
// full-page-redirects to it; the provider redirects back to `/auth/idp-callback`
// carrying the intent id+token, which `socialFinish` exchanges for Streamko
// tokens (same shape as email-OTP, persisted the same way).

export type SocialProvider = 'google' | 'apple' | 'facebook';

/** sessionStorage key that carries the chosen provider across the full-page redirect. */
export const SOCIAL_PROVIDER_KEY = 'streamko-social-provider';

export interface SocialStartResponse {
  /** Provider authorization URL (accounts.google.com/…) — open it top-level. */
  authUrl: string;
}

/**
 * POST /auth/social/start — begins an IdP intent for `provider`. `redirectUri`
 * is this app's own `/auth/idp-callback` on the current origin; the backend
 * rejects any value not in its server-side allow-list, so the intent id+token
 * can only ever come back to us.
 */
export function socialStart(provider: SocialProvider, redirectUri: string): Promise<SocialStartResponse> {
  return request<SocialStartResponse>('/auth/social/start', { provider, redirectUri });
}

/**
 * POST /auth/social/finish — exchanges the finished IdP intent (provider-verified
 * identity) for Streamko tokens, persisting them like the email-OTP verify path.
 */
export async function socialFinish(input: {
  provider: SocialProvider;
  intentId: string;
  token: string;
}): Promise<EmailOtpAuthResponse> {
  const data = await request<EmailOtpAuthResponse>('/auth/social/finish', input);
  persistFromAuthResponse(data.accessToken, data.refreshToken, data.expiresIn, data.idToken);
  if (data.user) setStoredUser(data.user);
  return data;
}

/** POST /auth/email/complete-profile — for brand-new accounts; creates the local org. */
export async function completeProfile(input: {
  firstName: string;
  lastName: string;
  organizationName: string;
}): Promise<CompleteProfileResponse> {
  const data = await request<CompleteProfileResponse>('/auth/email/complete-profile', input, true);
  if (data.user) setStoredUser(data.user);
  return data;
}

// ── Token refresh ─────────────────────────────────────────────────────────--

interface Discovery {
  tokenEndpoint: string;
  clientId: string;
}

let _discovery: Discovery | null = null;
let _discoveryPromise: Promise<Discovery> | null = null;
let _refreshInFlight: Promise<StoredTokens | null> | null = null;

/**
 * Resolve Zitadel's token endpoint + the customer client id. The issuer/client
 * id come from the backend's runtime config (same source oidcClient uses); the
 * token endpoint comes from OIDC discovery on that issuer. Cached for the
 * session.
 */
async function getDiscovery(): Promise<Discovery> {
  if (_discovery) return _discovery;
  if (!_discoveryPromise) {
    _discoveryPromise = (async () => {
      const cfgRes = await fetch(`${apiBase()}/auth/config`, { credentials: 'omit' });
      if (!cfgRes.ok) throw new Error(`/auth/config returned ${cfgRes.status}`);
      const cfg = (await cfgRes.json()) as { customer: { issuer: string; clientId: string } };
      const issuer = cfg.customer.issuer.replace(/\/$/, '');
      const discRes = await fetch(`${issuer}/.well-known/openid-configuration`);
      if (!discRes.ok) throw new Error(`OIDC discovery returned ${discRes.status}`);
      const disc = (await discRes.json()) as { token_endpoint: string };
      _discovery = { tokenEndpoint: disc.token_endpoint, clientId: cfg.customer.clientId };
      return _discovery;
    })().catch((err) => {
      _discoveryPromise = null; // allow retry
      throw err;
    });
  }
  return _discoveryPromise;
}

async function doRefresh(refreshToken: string): Promise<StoredTokens | null> {
  const { tokenEndpoint, clientId } = await getDiscovery();
  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
    }),
  });

  if (!res.ok) {
    // 400/401 mean the grant was rejected (revoked / expired refresh token) —
    // the session is dead, clear it so the user is routed to sign-in. Other
    // statuses (5xx, network) are transient: keep the tokens and try later.
    if (res.status === 400 || res.status === 401) signOutEmail();
    return null;
  }

  const data = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in?: number;
  };
  const tokens: StoredTokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token || refreshToken,
    idToken: data.id_token,
    expiresAt: Date.now() + (data.expires_in ?? 3600) * 1000,
  };
  storeTokens(tokens);
  return tokens;
}

/** Coalesce concurrent refreshes so a burst of API calls triggers one grant. */
function coalescedRefresh(refreshToken: string): Promise<StoredTokens | null> {
  if (!_refreshInFlight) {
    _refreshInFlight = doRefresh(refreshToken)
      .catch(() => null)
      .finally(() => {
        _refreshInFlight = null;
      });
  }
  return _refreshInFlight;
}

/**
 * Current access token for the email session, refreshing if it's within the
 * skew window. Returns null when there's no email session (so the caller can
 * fall back to OIDC) or when a hard-expired token can't be refreshed.
 */
export async function getAccessToken(): Promise<string | null> {
  const tokens = loadTokens();
  if (!tokens) return null;

  if (Date.now() >= tokens.expiresAt - REFRESH_SKEW_MS) {
    if (!tokens.refreshToken) {
      return Date.now() < tokens.expiresAt ? tokens.accessToken : null;
    }
    const refreshed = await coalescedRefresh(tokens.refreshToken);
    if (refreshed) return refreshed.accessToken;
    // Transient failure — hand back the old token if it's not hard-expired yet.
    return Date.now() < tokens.expiresAt ? tokens.accessToken : null;
  }

  return tokens.accessToken;
}
